import React, { useState, useCallback } from "react";
import axios from "axios";
import { Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;
const CHANGE_TIMEOUT_MS = 25000;

/**
 * Baris input password dengan tombol lihat/sembunyikan.
 * Didefinisikan di level modul (bukan di dalam komponen induk) supaya input
 * tidak di-mount ulang tiap ketikan, yang menutup keyboard HP.
 */
function PwInput({ id, label, value, onChange, show, onToggleShow, testid, autoFocus }) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="text-sm font-medium text-gray-700">{label}</label>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          className="pr-10"
          data-testid={testid}
          autoComplete="off"
          autoFocus={autoFocus}
        />
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          tabIndex={-1}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

/**
 * Layar wajib ganti password (Okt 2026).
 *
 * Tampil menggantikan seluruh aplikasi untuk akun yang login dengan password
 * universal lama (flag `must_change_password` dari server). Memakai endpoint
 * yang sama dengan Ganti Password biasa: POST /api/users/change-password.
 *
 * - `knownOldPassword`: password yang baru saja diketik di halaman login.
 *   Kalau ada, user tidak perlu mengetik password lama lagi. Kalau aplikasi
 *   dibuka ulang (nilai ini hilang dari memori), kolom Password Lama muncul.
 * - Sengaja TIDAK memakai toast: Toaster berada di dalam router yang sedang
 *   tidak tampil, jadi pesan error ditampilkan langsung di layar ini.
 */
export default function ForcePasswordChange({ user, knownOldPassword, onDone, onLogout }) {
  const needOld = !knownOldPassword;
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onChangeOld = useCallback((e) => setOldPassword(e.target.value), []);
  const onChangeNew = useCallback((e) => setNewPassword(e.target.value), []);
  const onChangeConfirm = useCallback((e) => setConfirmPassword(e.target.value), []);
  const toggleOld = useCallback(() => setShowOld((s) => !s), []);
  const toggleNew = useCallback(() => setShowNew((s) => !s), []);
  const toggleConfirm = useCallback(() => setShowConfirm((s) => !s), []);

  // Cermin validasi server (server tetap sumber kebenaran).
  const validate = () => {
    if (needOld && !oldPassword) return "Password lama wajib diisi";
    if (newPassword.length < 6) return "Password baru minimal 6 karakter";
    if (!/\d/.test(newPassword)) return "Password baru harus mengandung minimal 1 angka";
    if (newPassword !== confirmPassword) return "Konfirmasi password tidak sama";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/users/change-password`,
        {
          old_password: needOld ? oldPassword : knownOldPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
        { timeout: CHANGE_TIMEOUT_MS }
      );
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      if (typeof onDone === "function") await onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && !needOld && /password lama salah/i.test(detail)) {
        // Sinyal lemah: penggantian bisa saja sudah berhasil di server tapi
        // jawabannya tidak sampai, jadi password lama yang tadi diketik sudah tidak berlaku.
        setError("Jika tadi sudah berhasil, tekan Keluar lalu masuk dengan password baru Anda.");
      } else if (typeof detail === "string") {
        setError(detail);
      } else if (!err?.response) {
        setError("Tidak ada koneksi. Cek sinyal lalu coba lagi.");
      } else {
        setError("Gagal mengubah password. Coba lagi.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && !!newPassword && !!confirmPassword && (!needOld || !!oldPassword);

  return (
    <div
      className="min-h-screen bg-gray-50 flex items-center justify-center p-4"
      data-testid="force-password-change"
    >
      <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-orange-50 p-3 rounded-lg">
            <Lock className="w-5 h-5 text-orange-600" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900">Buat Password Baru</h1>
        </div>

        <p className="text-sm text-gray-600 mb-5">
          {user?.name ? `Halo ${user.name}. ` : ""}
          Demi keamanan, password lama akun ini harus diganti sebelum Anda bisa masuk.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {needOld && (
            <PwInput
              id="fpc-old"
              label="Password Lama"
              value={oldPassword}
              onChange={onChangeOld}
              show={showOld}
              onToggleShow={toggleOld}
              testid="fpc-old-input"
            />
          )}
          <PwInput
            id="fpc-new"
            label="Password Baru"
            value={newPassword}
            onChange={onChangeNew}
            show={showNew}
            onToggleShow={toggleNew}
            testid="fpc-new-input"
            autoFocus={!needOld}
          />
          <p className="text-xs text-gray-500 -mt-2">Minimal 6 karakter dan mengandung minimal 1 angka.</p>
          <PwInput
            id="fpc-confirm"
            label="Konfirmasi Password Baru"
            value={confirmPassword}
            onChange={onChangeConfirm}
            show={showConfirm}
            onToggleShow={toggleConfirm}
            testid="fpc-confirm-input"
          />

          {error && (
            <p className="text-sm text-red-600" role="alert" data-testid="fpc-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={!canSubmit}
            data-testid="fpc-submit-btn"
            className="w-full bg-orange-600 hover:bg-orange-700"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Menyimpan...
              </>
            ) : (
              "Simpan Password Baru"
            )}
          </Button>
        </form>

        <button
          type="button"
          onClick={onLogout}
          className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700"
          data-testid="fpc-logout-btn"
        >
          Keluar
        </button>
      </div>
    </div>
  );
}
